import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  useTheme,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PublicIcon from '@mui/icons-material/Public';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import CustomerServiceModal from '../components/contact/CustomerServiceModal';

const Certification = () => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const certificationTypes = [
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />,
      title: '专业翻译认证',
      subtitle: '权威机构认可',
      description: '专业翻译认证服务，主要支持各种留学、出国、移民等场景的专业翻译文件审核和签章认证，确保文件的法律效力和国际认可度',
      features: [
        '留学申请材料认证',
        '移民文件认证', 
        '签证申请材料认证',
        '工作许可文件认证',
        '学历学位认证',
        '商务文件认证'
      ],
      color: theme.palette.primary.main,
      badge: '专业认证',
      scenarios: [
        '美国留学申请',
        '加拿大移民',
        '英国工作签证',
        '欧盟学历认证',
        '新西兰技术移民',
        '日本留学签证'
      ]
    },
    {
      icon: <PublicIcon sx={{ fontSize: 64, color: '#FF6B35' }} />,
      title: 'NAATI认证翻译',
      subtitle: '澳新官方认可',
      description: 'NAATI（澳大利亚国家翻译员和口译员认证机构）官方认证翻译服务，专门针对澳大利亚和新西兰移民、留学、工作等申请提供具有法律效力的认证翻译',
      features: [
        '澳洲移民申请必备',
        '澳洲留学签证专用',
        '澳洲工作许可认证',
        '澳洲永居申请',
        '澳洲入籍申请',
        '澳洲学历认证',
        '新西兰移民申请',
        '新西兰学历认证'
      ],
      color: '#FF6B35',
      badge: 'NAATI官方',
      scenarios: [
        '澳洲技术移民189签证',
        '澳洲州担保190签证',
        '澳洲雇主担保482签证',
        '澳洲学生签证500',
        '澳洲配偶签证820/801',
        '新西兰技能移民签证'
      ]
    }
  ];

  const naatiAdvantages = [
    {
      title: '官方权威',
      description: 'NAATI是澳大利亚政府指定的翻译认证机构，认证具有法律效力'
    },
    {
      title: '国际认可',
      description: 'NAATI认证在澳大利亚和新西兰被官方认可，在全球英联邦国家广泛接受'
    },
    {
      title: '移民必备',
      description: '澳大利亚和新西兰移民申请必须提供NAATI认证的翻译文件'
    },
    {
      title: '质量保证',
      description: '严格的认证标准确保翻译质量和准确性'
    }
  ];

  const certificationProcess = [
    {
      step: '1',
      title: '文件提交',
      description: '上传需要认证翻译的原始文件'
    },
    {
      step: '2',
      title: 'AI翻译',
      description: '使用智能翻译引擎进行初步翻译'
    },
    {
      step: '3',
      title: '人工校对',
      description: '由认证译员进行专业翻译校对'
    },
    {
      step: '4',
      title: '质量审核',
      description: '多重质量检查和校对'
    },
    {
      step: '5',
      title: '认证盖章',
      description: '加盖官方认证印章和签名'
    },
    {
      step: '6',
      title: '交付文件',
      description: '提供认证翻译件和证书'
    }
  ];

  const faqs = [
    {
      question: '专业翻译认证和NAATI翻译认证有什么区别？',
      answer: '专业翻译认证是指由具备相关资质的译员提供的认证翻译服务，适用于大多数国际场合。而NAATI翻译认证是澳大利亚国家翻译员和口译员认证机构的官方认证，英文全称是National Accreditation Authority for Translators and Interpreters，具有更高的权威性和法律效力，专门针对澳大利亚和新西兰的官方申请。NAATI认证在移民、留学等官方申请中是强制要求，而专业翻译认证则适用范围更广泛。'
    },
    {
      question: 'NAATI认证翻译适用于哪些场景？',
      answer: 'NAATI认证翻译主要适用于：澳大利亚和新西兰移民申请、学历认证、工作签证申请、永久居留申请、入籍申请、法庭文件、官方证明等需要官方认可的场合。新西兰移民局认可NAATI认证翻译，特别是在技能移民和学历认证方面。'
    },
    {
      question: '认证翻译需要多少时间？',
      answer: '认证翻译的时间取决于文件类型、页数和紧急程度。一般情况下：普通认证翻译需要1-2个工作日，NAATI认证翻译需要1-2个工作日。如果是紧急件，我们可以提供4小时内的加急服务。复杂的法律文件或大量文档可能需要更长时间。我们会在接收文件后给出准确的交付时间。'
    },
    {
      question: '认证翻译的有效期是多久？',
      answer: '认证翻译本身没有固定的有效期，但不同机构对文件的时效性要求不同。一般来说，认证翻译在翻译完成后的2-3年内都被认为是有效的，具体要求请咨询相关机构。'
    },
    {
      question: '认证翻译的费用如何计算？',
      answer: '认证翻译费用通常根据文件类型、页数、翻译方式（是否需要认证）、紧急程度等因素计算。专业认证附赠人工审核加上认证翻译由于其权威性，费用会比普通翻译略高，但物有所值。'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* 页面标题 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <VerifiedUserIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 2
          }}
        >
          专业翻译认证
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
        >
          提供专业级翻译认证、澳大利亚NAATI翻译认证，确保您的文件具有法律效力
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label="专业翻译认证" 
            color="primary"
            sx={{ fontWeight: 600 }}
          />
          <Chip 
            label="澳大利亚NAATI翻译认证" 
            sx={{ 
              backgroundColor: '#FF6B35', 
              color: 'white',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#E55A2B' }
            }} 
          />
        </Box>
      </Box>

      {/* 认证类型 */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        {certificationTypes.map((cert, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: '100%',
                p: 4,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                position: 'relative',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 12px 35px ${cert.color}20`,
                  border: `2px solid ${cert.color}30`
                }
              }}
            >
              <Chip 
                label={cert.badge}
                size="small"
                sx={{ 
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  backgroundColor: cert.color,
                  color: 'white',
                  fontWeight: 600
                }}
              />
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, textAlign: 'center' }}>
                  {cert.icon}
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                    {cert.title}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: cert.color, mb: 2, fontWeight: 600 }}>
                    {cert.subtitle}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {cert.description}
                  </Typography>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: cert.color }}>
                  认证范围：
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {cert.features.map((feature, idx) => (
                    <Chip 
                      key={idx}
                      label={feature}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: cert.color + '50',
                        color: cert.color,
                        '&:hover': {
                          backgroundColor: cert.color + '10'
                        }
                      }}
                    />
                  ))}
                </Box>

                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: cert.color }}>
                  适用场景：
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {cert.scenarios.map((scenario, idx) => (
                    <Chip 
                      key={idx}
                      label={scenario}
                      size="small"
                      sx={{ 
                        backgroundColor: cert.color + '15',
                        color: cert.color,
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: cert.color + '25'
                        }
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* NAATI认证优势 */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
          color: 'white',
          p: 6,
          borderRadius: 3,
          mb: 24,
          mt: 8,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 35px rgba(255, 107, 53, 0.3)'
          }
        }}
      >
        <Typography variant="h4" sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}>
          <StarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          NAATI认证优势 
          <StarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        </Typography>
        <Grid container spacing={3}>
          {naatiAdvantages.map((advantage, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {advantage.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {advantage.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* 认证流程 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          认证流程
        </Typography>
        <Grid container spacing={3}>
          {certificationProcess.map((process, index) => (
            <Grid item xs={12} sm={4} md={2} key={index}>
              <Card
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 3,
                  height: '100%',
                  border: `2px solid ${theme.palette.primary.main}20`,
                  '&:hover': {
                    border: `2px solid ${theme.palette.primary.main}`,
                    transform: 'translateY(-4px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }}
                >
                  {process.step}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {process.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {process.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* 常见问题 */}
      <Box sx={{ mb: 8, mt: 24 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 4, 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          常见问题
        </Typography>
        {faqs.map((faq, index) => (
          <Accordion 
            key={index}
            sx={{ 
              mb: 2, 
              borderRadius: 2,
              '&:before': {
                display: 'none',
              },
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                px: 3,
                py: 2,
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0'
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* 立即开始 */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          p: 6,
          borderRadius: 3,
          textAlign: 'center',
          mb: 12,
          mt: 24
        }}
      >
        <VerifiedUserIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          获取专业认证翻译
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          我们的认证译员团队具备相关资质和丰富经验，确保您的翻译文件符合官方标准，
          为您的澳大利亚、新西兰以及其它国家和地区的移民、留学、工作申请提供有力支持。
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleOpenModal}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            borderRadius: 3,
            boxShadow: '0 8px 25px rgba(67, 83, 255, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 35px rgba(67, 83, 255, 0.4)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          立即申请认证翻译
        </Button>
      </Card>

      {/* 客服弹窗 */}
      <CustomerServiceModal 
        open={modalOpen} 
        onClose={handleCloseModal} 
      />
    </Container>
  );
};

export default Certification; 